import { useState } from 'react';
import { Skeleton, Stack, Text, UnstyledButton } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { Story, StoryKind } from '@nelnela/contracts';
import { api } from '../../lib/api';
import { readBadge, readMinutes, shelfBand, shelfDimmed } from '../../lib/library';
import { speakerOrder } from '../../lib/piece-player';
import { storyTitle } from '../../lib/reader';

/**
 * The shared shelf on `/read` (M6b step 7): the curated pieces, beside the learner's own stories.
 *
 * Not a nav entry of its own and not on `Library` (which is the WATCH shelf, and mixing content
 * types there would blur the one screen whose job is unambiguous). `/read` is already the reading
 * home; this is its second half.
 *
 * A read piece keeps its place with a quiet marker rather than vanishing. Re-reading is a
 * legitimate thing to do, and hiding what you have read makes a shelf look empty exactly when it
 * has been most used.
 */

const SEGMENTS: Array<{ value: StoryKind | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'story', label: 'Stories' },
  { value: 'dialogue', label: 'Dialogues' },
  { value: 'culture', label: 'Culture' },
];

export interface LibraryShelfProps {
  targetLang: string;
  baseLang: string;
  /**
   * The learner's level estimate, or null when nothing has been measured yet.
   *
   * NOT currently a meaningful filter, and that is deliberate rather than an oversight: the
   * measured piece levels do not yet vary (see `docs/M6B-HANDOFF.md` § "The level is not yet a
   * level"), so every piece passes. The filter is wired because it is where the filter goes; no
   * copy on this shelf claims the level is a measurement.
   */
  levelEstimate: number | null;
}

/** How far above the learner's estimate the shelf still offers a piece. */
export const LEVEL_HEADROOM = 10;

export function LibraryShelf({ targetLang, baseLang, levelEstimate }: LibraryShelfProps) {
  const [kind, setKind] = useState<StoryKind | 'all'>('all');
  const levelMax = levelEstimate === null ? undefined : levelEstimate + LEVEL_HEADROOM;

  const q = useQuery({
    queryKey: ['library-pieces', targetLang, baseLang, kind, levelMax],
    queryFn: () =>
      api.libraryPieces({
        targetLang,
        baseLang,
        ...(kind === 'all' ? {} : { kind }),
        ...(levelMax === undefined ? {} : { levelMax }),
        limit: 40,
      }),
    staleTime: 60_000,
  });

  const pieces = q.data?.items ?? [];
  return (
    <Stack gap={8}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <Text fz={11} c="var(--nn-faint)" tt="uppercase" style={{ letterSpacing: '.06em' }}>
          The library
        </Text>
        <Text fz={11} c="var(--nn-faint)">
          written for everyone
        </Text>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {SEGMENTS.map((s) => (
          <UnstyledButton
            key={s.value}
            onClick={() => setKind(s.value)}
            aria-pressed={kind === s.value}
            style={{
              padding: '4px 10px',
              borderRadius: 999,
              fontSize: 11.5,
              border: '1px solid var(--nn-hairline)',
              color: kind === s.value ? 'var(--nn-text-strong)' : 'var(--nn-muted)',
              background: kind === s.value ? 'var(--nn-panel-2)' : 'transparent',
            }}
          >
            {s.label}
          </UnstyledButton>
        ))}
      </div>

      {q.isPending ? (
        <Stack gap={8}>
          <Skeleton height={52} radius="sm" />
          <Skeleton height={52} radius="sm" />
          <Skeleton height={52} radius="sm" />
        </Stack>
      ) : q.isError ? (
        <Text fz={12} c="var(--nn-faint)">
          The library will be here when the connection is.
        </Text>
      ) : pieces.length === 0 ? (
        <Text fz={12} c="var(--nn-faint)">
          {kind === 'all'
            ? 'Nothing on the shelf yet.'
            : `No ${SEGMENTS.find((s) => s.value === kind)?.label.toLowerCase()} yet.`}
        </Text>
      ) : (
        <Stack gap={6}>
          {pieces.map((p) => (
            <PieceCard key={p.id} piece={p} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

function PieceCard({ piece }: { piece: Story }) {
  const speakers = speakerOrder(piece);
  const badge = readBadge(piece);
  const meta = [
    `~${readMinutes(piece)} min`,
    shelfBand(piece.level),
    piece.kind === 'dialogue' && speakers.length === 2 ? speakers.join(' · ') : null,
  ].filter(Boolean);

  return (
    <UnstyledButton
      component={Link}
      to={`/read/${piece.id}`}
      style={{
        display: 'block',
        padding: '10px 12px',
        borderRadius: 8,
        border: '1px solid var(--nn-hairline)',
        background: 'var(--nn-panel)',
        // A read piece is dimmed, not removed. Re-reading is legitimate, and hiding what has been
        // read empties the shelf exactly when it has been used most. A piece walked out of is
        // NOT dimmed (M6e §8): it is unfinished business, and dimming it hides the one card on
        // the shelf the learner most likely meant to come back to.
        opacity: shelfDimmed(piece) ? 0.62 : 1,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span aria-hidden style={{ fontSize: 13 }}>
          {piece.kind === 'dialogue' ? '💬' : piece.kind === 'culture' ? '◈' : '📖'}
        </span>
        <Text fz={13.5} fw={500} truncate style={{ flex: 1, minWidth: 0 }}>
          {piece.kind === 'culture' ? (piece.title ?? 'A note') : storyTitle(piece)}
        </Text>
        {badge && (
          <Text
            fz={10.5}
            // "left early" is the one badge that is a nudge rather than a note, so it is the one
            // that gets the text colour. The rest stay faint — a shelf of loud labels reads as a
            // dashboard, and this is a bookshelf.
            c={badge === 'left early' ? 'var(--nn-muted)' : 'var(--nn-faint)'}
            style={{ flexShrink: 0 }}
          >
            {badge}
          </Text>
        )}
      </div>
      <Text fz={11.5} c="dimmed" mt={2} truncate>
        {meta.join(' · ')}
      </Text>
    </UnstyledButton>
  );
}
